import { Injectable } from '@angular/core';

export interface ExportColumn {
  field: string;
  header: string;
  type?: 'text' | 'number' | 'currency' | 'percent' | 'date';
  width?: string;
}

export interface ExportOptions {
  fileName: string;
  title: string;
  subtitle?: string;
  columns: ExportColumn[];
  headerColor?: string;
}

@Injectable({ providedIn: 'root' })
export class ExportService {

  exportarExcel<T>(dados: T[], opcoes: ExportOptions): void {
    if (!dados.length) return;

    const cor = opcoes.headerColor || '#667eea';
    let html = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">';
    html += '<head><meta charset="utf-8"><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>';
    html += `<x:Name>${opcoes.title}</x:Name>`;
    html += '<x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>';
    html += '</x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head>';
    html += '<body><table border="1">';

    html += `<tr style="background-color:${cor};color:white;font-weight:bold;">`;
    opcoes.columns.forEach(col => {
      html += `<td${col.width ? ` width="${col.width}"` : ''}>${col.header}</td>`;
    });
    html += '</tr>';

    dados.forEach((row: any) => {
      html += '<tr>';
      opcoes.columns.forEach(col => {
        const val = row[col.field] ?? '';
        const formatted = this.formatValue(val, col.type);
        const align = (col.type === 'number' || col.type === 'currency' || col.type === 'percent') ? ' style="text-align:right;"' : '';
        html += `<td${align}>${formatted}</td>`;
      });
      html += '</tr>';
    });

    html += '</table></body></html>';

    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${opcoes.fileName}.xls`;
    link.click();
    URL.revokeObjectURL(url);
  }

  exportarPDF<T>(dados: T[], opcoes: ExportOptions): void {
    if (!dados.length) return;

    const cor = opcoes.headerColor || '#667eea';
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    let html = '<!DOCTYPE html><html><head><meta charset="utf-8">';
    html += `<title>${opcoes.title}</title>`;
    html += '<style>';
    html += 'body { font-family: Arial, sans-serif; margin: 20px; color: #333; }';
    html += `h1 { color: ${cor}; font-size: 20px; border-bottom: 2px solid ${cor}; padding-bottom: 8px; }`;
    html += 'p.sub { color: #718096; font-size: 12px; margin-top: 4px; }';
    html += 'table { width: 100%; border-collapse: collapse; font-size: 10px; margin-top: 15px; }';
    html += `th { background: ${cor}; color: white; padding: 7px 5px; text-align: left; }`;
    html += 'td { padding: 5px; border-bottom: 1px solid #e2e8f0; }';
    html += 'tr:nth-child(even) { background: #f7fafc; }';
    html += '.text-right { text-align: right; }';
    html += '.footer { margin-top: 20px; font-size: 10px; color: #a0aec0; border-top: 1px solid #e2e8f0; padding-top: 8px; }';
    html += '@media print { @page { size: landscape; } }';
    html += '</style></head><body>';

    html += `<h1>${opcoes.title}</h1>`;
    if (opcoes.subtitle) html += `<p class="sub">${opcoes.subtitle}</p>`;
    html += `<p class="sub">Gerado em: ${new Date().toLocaleString('pt-BR')} — Total: ${dados.length} registros</p>`;

    html += '<table><tr>';
    opcoes.columns.forEach(col => { html += `<th>${col.header}</th>`; });
    html += '</tr>';

    dados.forEach((row: any) => {
      html += '<tr>';
      opcoes.columns.forEach(col => {
        const val = row[col.field] ?? '';
        const formatted = this.formatValue(val, col.type);
        const align = (col.type === 'number' || col.type === 'currency' || col.type === 'percent') ? ' class="text-right"' : '';
        html += `<td${align}>${formatted}</td>`;
      });
      html += '</tr>';
    });

    html += '</table>';
    html += '<div class="footer">Monitor Gerencial — Relatório gerado automaticamente</div>';
    html += '</body></html>';

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  }

  exportarCSV<T>(dados: T[], opcoes: ExportOptions): void {
    if (!dados.length) return;

    const sep = ';';
    const headers = opcoes.columns.map(c => `"${c.header}"`).join(sep);
    const rows = dados.map((row: any) =>
      opcoes.columns.map(col => {
        const val = row[col.field] ?? '';
        return typeof val === 'string' ? `"${val.replace(/"/g, '""')}"` : val;
      }).join(sep)
    );

    const csv = '﻿' + headers + '\n' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${opcoes.fileName}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  private formatValue(val: any, type?: string): string {
    if (val === null || val === undefined) return '';
    switch (type) {
      case 'currency': return `R$ ${Number(val).toFixed(2).replace('.', ',')}`;
      case 'percent': return `${Number(val).toFixed(1)}%`;
      case 'number': return Number(val).toLocaleString('pt-BR');
      case 'date': return val ? new Date(val).toLocaleString('pt-BR') : '';
      default: return String(val);
    }
  }
}
